import {
  isBlockedExecutable,
  getBlockedFileErrorMessage,
} from '../constants/security';

/**
 * Security-only validation for file uploads (NO type filtering).
 *
 * Validates:
 * 1. Executable/archive blocking (using BLOCKED_EXECUTABLE_EXTENSIONS)
 * 2. File size limits
 *
 * Does NOT validate file types - that's handled by HTML accept attribute
 * or native file picker filters.
 */

/** Default maximum upload size in MB for section-level file pickers */
export const MAX_UPLOAD_SIZE_MB = 150;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export interface FileToValidate {
  name: string;
  size: number;
  type?: string;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export function validateFileForSecurity(
  file: FileToValidate,
  maxSizeInMB: number = MAX_UPLOAD_SIZE_MB,
): ValidationResult {
  // Security check: Blocked executable/archive extensions
  if (isBlockedExecutable(file.name)) {
    logSecurityViolation(file);

    return {
      valid: false,
      error: getBlockedFileErrorMessage(file.name),
    };
  }

  // Size check
  const maxSizeBytes = maxSizeInMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeInMB}MB limit.`,
    };
  }

  return { valid: true };
}

/**
 * Log a security violation with truncated filename to prevent PII exposure.
 * Uses __DEV__ guard so security probing info is not visible in production.
 */
function logSecurityViolation(file: FileToValidate): void {
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    console.warn('Security: Executable file blocked', {
      filename: truncateFilename(file.name),
      sizeKB: Math.round(file.size / 1024),
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Truncate filename to prevent PII exposure in logs.
 * Returns first 20 chars of basename + "..." + extension (max 8 chars).
 *
 * Example: "VeryLongConfidentialDocu2024.exe" -> "VeryLongConfidentialD....exe"
 */
export function truncateFilename(filename: string): string {
  if (filename.length <= 30) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');
  const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
  const basename =
    lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;

  if (basename.length <= 20) {
    return filename; // No truncation needed
  }

  // Truncate basename, preserve extension (max 8 chars of extension)
  const truncatedExt =
    extension.length > 8
      ? extension.substring(extension.length - 8)
      : extension;
  return basename.substring(0, 20) + '...' + truncatedExt;
}

/**
 * Build an aggregated error message for multiple blocked files.
 * Shows up to 3 truncated filenames, then "and X more".
 *
 * Single file: "EXE files are not allowed for security reasons..."
 * Multiple files: "3 files were blocked for security reasons. Blocked files: virus.exe, malware.zip, script.sh"
 * Many files: "5 files were blocked... Blocked files: virus.exe, malware.zip, script.sh and 2 more"
 */
export function buildAggregatedBlockedMessage(
  blockedFileNames: string[],
): string {
  if (blockedFileNames.length === 0) {
    return '';
  }

  if (blockedFileNames.length === 1) {
    return getBlockedFileErrorMessage(blockedFileNames[0]);
  }

  const displayNames = blockedFileNames
    .slice(0, 3)
    .map(n => truncateFilename(n))
    .join(', ');
  const remaining = blockedFileNames.length - 3;
  const suffix = remaining > 0 ? ` and ${remaining} more` : '';

  return `${blockedFileNames.length} files were blocked for security reasons. Blocked files: ${displayNames}${suffix}`;
}
